/** @universal knowledge/universals/engines.md#customs-calculator-engine */
import { type CBRRates, type CurrencyCode, COUNTRY_CURRENCY } from "./currencyRates";
import { calcRecyclingFeeKw, recyclingDetails } from "./tariffs/recyclingFee";
import { resolveHybridUtilPowerKw, resolveThirtyMinPowerKw, type ThirtyMinPower } from "./tariffs/thirtyMinutePower";
import { hpToKw, kwToHp } from "./units/power";
import {
  CUSTOMS_PROCESSING_FEE,
  ETS_UNDER3,
  ETS_3TO5,
  ETS_OVER5,
  DUTY_PETROL_UNDER3,
  DUTY_PETROL_3TO7,
  DUTY_PETROL_OVER7,
  DUTY_DIESEL_UNDER3_PCT,
  DUTY_DIESEL_3TO7,
  DUTY_DIESEL_OVER7,
  ELECTRIC_DUTY_RATE,
  EXCISE_RATES,
  VAT_RATE,
  FIXED_COSTS,
  DELIVERY_CITY,
} from "./tariffs";

/* ── Типы ─────────────────────────────────────────────────────────── */

export type CarAge = "under3" | "3to5" | "5to7" | "over7";
export type BuyerType = "individual" | "company";
export type Country = "china" | "korea" | "japan";
/**
 * Тип двигателя для классификации по нормативам РФ.
 *
 * @rule Семантика типов:
 *   - 'petrol' — бензиновый ДВС
 *   - 'diesel' — дизельный ДВС
 *   - 'hybrid' — параллельный/смешанный гибрид (HEV/PHEV — Toyota Camry Hybrid,
 *     RAV4 Hybrid, BMW X5 plug-in). ДВС вращает колёса. По ТН ВЭД 8703 22/23
 *     классифицируется как обычный ДВС с электропомощью. Пошлина и утильсбор
 *     рассчитываются по таблицам бензина (по объёму ДВС).
 *   - 'series_hybrid' — последовательный гибрид (REEV/EREV — Lixiang L6/L7/L8/L9,
 *     Voyah Free/Passion/Dream EVR, Aito M5/M7/M9 EREV, Zeekr 9X). ДВС работает
 *     ТОЛЬКО как генератор, не вращает колёса. По ТН ВЭД 8703 80 000 5
 *     (детализация введена Решением Коллегии ЕЭК №81 от 23.09.2025).
 *     Пошлина 15%, утильсбор по EV-пунктам (1/3) от 30-минутной мощности.
 *     Льгота физлица — 30-минутная мощность ≤ 58,84 кВт.
 *   - 'electric' — чистый электромобиль (BEV — Tesla, Zeekr 001/007, Nio).
 *     ТН ВЭД 8703 80 000 3. Пошлина 15%, утильсбор и акциз от 30-минутной мощности.
 *
 * @rule Используй хелпер `useEvTariff(engineType)` чтобы единообразно проверять
 *       "это EV-режим расчёта?" — true для 'electric' и 'series_hybrid'.
 */
export type EngineType = "petrol" | "diesel" | "hybrid" | "series_hybrid" | "electric";

/**
 * Возвращает true для типов двигателя, к которым применяется EV-тариф:
 * пошлина 15% и утильсбор по EV-пунктам 1/3 справочника ПП 1291.
 *
 * @rule Это единственное место где определяется EV-режим. Все ветки в
 *       calcDuty / calculateTotal должны использовать
 *       этот хелпер, не дублировать условие.
 */
export function useEvTariff(engineType: EngineType): boolean {
  return engineType === "electric" || engineType === "series_hybrid";
}

export interface CalcInput {
  priceInCurrency: number;
  currencyCode: CurrencyCode;
  engineVolume: number;
  enginePower: number;
  carAge: CarAge;
  buyerType: BuyerType;
  personalUse: boolean;
  country?: Country;
  engineType?: EngineType; // default = 'petrol'
  /**
   * 30-минутная мощность электромотора из документов (ОТТС / СБКТС / ЭПТС), кВт. Для
   * electric / series_hybrid — мотора машины; для гибрида с известными мощностями ДВС и мотора —
   * его электромотора. Нет — оценка: пиковая × 0,45 (thirtyMinutePower.ts).
   */
  power30MinKw?: number;
  /**
   * Гибрид (HEV / PHEV / мягкий): мощность ДВС, л.с. Вместе с hybridMotorPowerHp включает расчёт
   * утильсбора по сумме «ДВС + 30-мин мотора» (решение В4, CAT-POWER-02). Нет любого из двух —
   * расчёт как раньше, по enginePower. Акциз и пошлина от этих полей не зависят.
   */
  hybridIcePowerHp?: number;
  /** Гибрид: пиковая мощность электромотора(ов), л.с. Работает только в паре с hybridIcePowerHp. */
  hybridMotorPowerHp?: number;
}

/**
 * @rule R13-hotfix (2026-05-18): CalcResult is a STABLE CONTRACT.
 *       Consumers outside this file rely on it being passthrough-friendly:
 *
 *       - `totalRub` and `breakdown` (with `{label,value,details?}` items)
 *         are consumed by `services/semantics/pricingExample.ts` to build
 *         MDX PricingExample blocks for SEO articles. New fee logic added
 *         to calculateTotal automatically surfaces in articles WITHOUT
 *         editing pricingExample — provided you keep appending rows to
 *         `breakdown` rather than introducing a parallel data field.
 *
 *       - Future article generators MUST also consume this shape via the
 *         same `buildPricingExample(topic, selectedVehicle?)` entry point.
 *         Do NOT add per-generator parsing of calculator output.
 *
 *       SAFE CHANGES (article generators absorb automatically):
 *         - add new `breakdown` items (new fee rows, new conditional logic)
 *         - rename items' `label`/`details` text
 *         - change `value` calculations
 *         - add new fields ALONGSIDE existing ones (e.g. `dealerMargin`)
 *
 *       BREAKING CHANGES (require coordinated update in pricingExample.ts):
 *         - rename `breakdown` to something else
 *         - change `breakdown` item shape (e.g. drop `label` or `value`)
 *         - rename `totalRub`
 *         - change `breakdown` from array to map/object
 *
 *       If you make a breaking change, search for "@rule R13-hotfix" in
 *       services/semantics/pricingExample.ts and update both files in
 *       the same commit.
 */
export interface CalcResult {
  totalRub: number;
  carPriceRub: number;
  breakdown: { label: string; value: number; details?: string }[];
  currencyRate: {
    code: CurrencyCode;
    rate: number;
    eurRate: number;
    date: string;
  };
  deliveryCity?: string;
  /**
   * Aggregated services & logistics sum in RUB. Equals the local `fixedTotal`
   * computed inside calculateTotal:
   *   - China: FIXED_COSTS.china.markup_rub (150 000 ₽)
   *   - Korea: round(importMarkupKrw × KRW_rate) + brokerRub + commissionRub
   *   - Japan: round(importMarkupJpy × JPY_rate) + brokerRub + commissionRub
   * Read by priceCalculator.ts::mapResult as the primary source for
   * `deliveryCost`. Optional — older CalcResult consumers ignore it.
   * History: introduced 2026-05-21 by batch-2026-05-21-calculator-customs-fix
   * prompt 02 (servicesTotalRub field added, original semantics aggregated
   * SBKTS+EPTS+broker+logistics for Korea/Japan); semantics repurposed by
   * prompts 03-04 to the current per-country shape above.
   */
  servicesTotalRub?: number;
}

/* ── Вспомогательные ──────────────────────────────────────────────── */

function findBracket<T extends Record<string, number>>(
  table: T[],
  value: number,
  key: string,
): T {
  return table.find((row) => value <= row[key])!;
}

function fmt(n: number, decimals = 0): string {
  return n.toLocaleString('ru-RU', { maximumFractionDigits: decimals, minimumFractionDigits: decimals });
}

/* ── 1. Сбор за таможенное оформление ─────────────────────────────── */
function calcCustomsProcessingFee(priceRub: number): number {
  const row = findBracket(CUSTOMS_PROCESSING_FEE, priceRub, "maxRub");
  return row.fee;
}

/* ── 2. ЕТС (физлица, только ДВС) ──────────────────────────────── */
function calcETS(
  priceEur: number,
  volume: number,
  age: CarAge,
  eurRate: number,
): number {
  if (age === "under3") {
    const row = findBracket(ETS_UNDER3, priceEur, "maxEur");
    const byPercent = priceEur * row.pct;
    const byVolume = volume * row.minPerCc;
    return Math.round(Math.max(byPercent, byVolume) * eurRate);
  }

  if (age === "3to5") {
    const row = findBracket(ETS_3TO5, volume, "maxCc");
    return Math.round(volume * row.rate * eurRate);
  }

  // 5to7 и over7 → таблица "старше 5 лет" для физлиц
  const row = findBracket(ETS_OVER5, volume, "maxCc");
  return Math.round(volume * row.rate * eurRate);
}

/* ── 3. Пошлина (юрлица + электро/EREV-физлица) ──────────────────── */
function calcDuty(
  priceRub: number,
  priceEur: number,
  volume: number,
  age: CarAge,
  eurRate: number,
  engineType: EngineType,
): number {
  // EV-тариф: 15% любой возраст. Применяется к electric (BEV, ТН ВЭД 8703 80 000 3)
  // И к series_hybrid (REEV/EREV, ТН ВЭД 8703 80 000 5 — Lixiang, Voyah, Aito M9, Zeekr 9X).
  // @rule Решение Коллегии ЕЭК №81 от 23.09.2025 + Решение Совета ЕЭК от 14.09.2021 № 80.
  if (useEvTariff(engineType)) {
    return Math.round(priceRub * ELECTRIC_DUTY_RATE);
  }

  // Параллельный гибрид (HEV/PHEV) трактуется как бензин — ТН ВЭД 8703 22/23.
  // Объём бензинового двигателя определяет ставку.
  const isDiesel = engineType === "diesel";

  if (age === "under3") {
    if (isDiesel) {
      return Math.round(priceRub * DUTY_DIESEL_UNDER3_PCT);
    }
    // Бензин/гибрид — зависит от объёма
    const row = findBracket(DUTY_PETROL_UNDER3, volume, "maxCc");
    return Math.round(priceRub * row.pct);
  }

  if (age === "3to5" || age === "5to7") {
    const table = isDiesel ? DUTY_DIESEL_3TO7 : DUTY_PETROL_3TO7;
    const row = findBracket(table, volume, "maxCc");
    const byPercent = priceEur * row.pct;
    const byVolume = volume * row.minPerCc;
    return Math.round(Math.max(byPercent, byVolume) * eurRate);
  }

  // over7
  const table = isDiesel ? DUTY_DIESEL_OVER7 : DUTY_PETROL_OVER7;
  const row = findBracket(table, volume, "maxCc");
  return Math.round(volume * row.rate * eurRate);
}

/* ── 4. Акциз ───────────────────────────────────────────────────── */
function calcExcise(powerHp: number): number {
  const row = findBracket(EXCISE_RATES, powerHp, "maxHp");
  return Math.round(powerHp * row.rate);
}

/**
 * Текст строки «Акциз».
 * @rule Для электромобиля база — 30-минутная мощность (письма Минфина 03-13-12/5697 и
 *       ФНС СД-4-3/1137@ 2021 г.), это видно в тексте; оценочная мощность помечена как оценка.
 */
function exciseDetails(powerHp: number, thirtyMin?: ThirtyMinPower): string {
  const row = findBracket(EXCISE_RATES, powerHp, "maxHp");
  const note = !thirtyMin ? "" : thirtyMin.source === "documents" ? " (30-мин мощность)" : " (30-мин мощность, оценка)";
  if (row.rate === 0) return `без акциза (до 90 л.с.)${note}`;
  return `${fmt(row.rate)} ₽/л.с. × ${fmt(powerHp, thirtyMin ? 1 : 0)} л.с.${note}`;
}

/* ── 5. Утилизационный сбор ───────────────────────────────────────── */
// Считается в src/lib/tariffs/recyclingFee.ts по проверенному справочнику ПП 1291 (кВт, редакции
// по годам). 30-минутная мощность EV/EREV — src/lib/tariffs/thirtyMinutePower.ts.
// Контрольная точка: Genesis GV80 (2497 см³, 304 л.с. = 223,59 кВт, до 3 лет, ред. 2026) →
// п.4, блок 2000–3000, 205,95–228 кВт → 131,04 → 2 620 800 ₽.

/* ── Главная функция расчёта ──────────────────────────────────────── */
export function calculateTotal(input: CalcInput, rates: CBRRates): CalcResult {
  const { priceInCurrency, currencyCode, engineVolume, enginePower, carAge, buyerType, personalUse, country } = input;
  const engineType = input.engineType ?? "petrol";
  const isEv = useEvTariff(engineType);
  // EV/EREV: 30-минутная мощность (документы или оценка пиковая × 0,45) — вход утильсбора.
  const thirtyMin = isEv ? resolveThirtyMinPowerKw(enginePower, input.power30MinKw) : undefined;
  // Гибрид с известными ДВС и мотором: «ДВС + 30-мин мотора» (В4). Нет данных — undefined, как раньше.
  const hybridUtil = isEv
    ? undefined
    : resolveHybridUtilPowerKw(input.hybridIcePowerHp, input.hybridMotorPowerHp, input.power30MinKw);

  const currencyRate = rates[currencyCode] as number;
  const eurRate = rates.EUR;
  const city = country ? DELIVERY_CITY[country] : undefined;

  const isChina = country === "china";
  const isKorea = country === "korea";
  const isJapan = country === "japan";

  // 1. Цена в рублях и евро. Для всех трёх стран надбавка добавляется ДО конвертации
  //    (входит в таможенную стоимость — фрахт до границы РФ).
  let effectivePriceCurrency = priceInCurrency;
  if (isChina) {
    effectivePriceCurrency += FIXED_COSTS.china.markup_cny;
  } else if (isKorea) {
    effectivePriceCurrency += FIXED_COSTS.korea.importMarkupKrw;
  } else if (isJapan) {
    effectivePriceCurrency += FIXED_COSTS.japan.importMarkupJpy;
  }
  const carPriceRub = Math.round(effectivePriceCurrency * currencyRate);
  const carPriceEur = carPriceRub / eurRate;

  // 2. Таможенное оформление
  const processingFee = calcCustomsProcessingFee(carPriceRub);
  const processingRow = findBracket(CUSTOMS_PROCESSING_FEE, carPriceRub, "maxRub");
  const processingDetails = `при стоимости авто до ${fmt(processingRow.maxRub === Infinity ? 999_999_999 : processingRow.maxRub)} ₽`;

  // 3. Таможенные платежи
  const breakdown: { label: string; value: number; details?: string }[] = [];

  if (isChina) {
    breakdown.push({ label: "Автомобиль", value: Math.round(priceInCurrency * currencyRate) });
    breakdown.push({ label: "Все расходы в Китае (оформление, страховка, погрузка)", value: Math.round(FIXED_COSTS.china.markup_cny * currencyRate) });
  } else if (isKorea) {
    breakdown.push({ label: "Автомобиль", value: Math.round(priceInCurrency * currencyRate) });
    // markup_krw rolls into the single "Услуги по импорту и доставке" row below (step 5).
  } else if (isJapan) {
    breakdown.push({ label: "Автомобиль", value: Math.round(priceInCurrency * currencyRate) });
    // markup_jpy rolls into the single "Услуги по импорту и доставке" row below (step 5).
  } else {
    breakdown.push({ label: "Автомобиль", value: carPriceRub });
  }

  breakdown.push({ label: "Таможенное оформление", value: processingFee, details: processingDetails });

  let customsTotal = 0;

  if (isEv) {
    // EV-режим (BEV или последовательный гибрид REEV/EREV):
    // и физлицо и юрлицо → пошлина 15% + акциз + НДС 22%.
    const duty = calcDuty(carPriceRub, carPriceEur, engineVolume, carAge, eurRate, engineType);
    const dutyLabel = engineType === "series_hybrid" ? "последовательный гибрид" : "электромобиль";
    const dutyDetails = `${(ELECTRIC_DUTY_RATE * 100).toFixed(0)}% от стоимости (${dutyLabel})`;

    // Акциз электромобиля — от 30-минутной мощности. Последовательный гибрид — пока от пиковой:
    // @todo ФНС (письмо БС-4-21/11239@ от 04.09.2023) для гибридов берёт сумму мощностей ДВС и
    //       электромотора; вопрос открыт вместе с В2 (knowledge/roadmap/tasks/2026-09-30-erev-duty-v2.md).
    const exciseThirtyMin = engineType === "electric" ? thirtyMin : undefined;
    const excisePowerHp = exciseThirtyMin ? kwToHp(exciseThirtyMin.kw) : enginePower;
    const excise = calcExcise(excisePowerHp);

    const vat = Math.round((carPriceRub + duty + excise) * VAT_RATE);
    const vatDetails = `22% × (стоимость + пошлина + акциз)`;

    breakdown.push({ label: "Таможенная пошлина", value: duty, details: dutyDetails });
    breakdown.push({ label: "Акциз", value: excise, details: exciseDetails(excisePowerHp, exciseThirtyMin) });
    breakdown.push({ label: "НДС 22%", value: vat, details: vatDetails });
    customsTotal = duty + excise + vat;
  } else if (buyerType === "individual") {
    // ДВС физлицо → ЕТС
    const ets = calcETS(carPriceEur, engineVolume, carAge, eurRate);
    let etsDetails: string;
    if (carAge === "under3") {
      const row = findBracket(ETS_UNDER3, carPriceEur, "maxEur");
      etsDetails = `max(${(row.pct * 100).toFixed(0)}% × ${fmt(Math.round(carPriceEur))} €; ${row.minPerCc} €/см³ × ${fmt(engineVolume)} см³) × ${fmt(eurRate, 2)} ₽/€`;
    } else if (carAge === "3to5") {
      const row = findBracket(ETS_3TO5, engineVolume, "maxCc");
      etsDetails = `${row.rate} €/см³ × ${fmt(engineVolume)} см³ × ${fmt(eurRate, 2)} ₽/€`;
    } else {
      const row = findBracket(ETS_OVER5, engineVolume, "maxCc");
      etsDetails = `${row.rate} €/см³ × ${fmt(engineVolume)} см³ × ${fmt(eurRate, 2)} ₽/€`;
    }
    breakdown.push({ label: "Единая таможенная ставка", value: ets, details: etsDetails });
    customsTotal = ets;
  } else {
    // ДВС юрлицо → пошлина + акциз + НДС
    const duty = calcDuty(carPriceRub, carPriceEur, engineVolume, carAge, eurRate, engineType);
    let dutyDetails: string;
    const isDiesel = engineType === "diesel";

    if (carAge === "under3") {
      if (isDiesel) {
        dutyDetails = `${(DUTY_DIESEL_UNDER3_PCT * 100).toFixed(0)}% от стоимости (дизель)`;
      } else {
        const row = findBracket(DUTY_PETROL_UNDER3, engineVolume, "maxCc");
        dutyDetails = `${(row.pct * 100).toFixed(1)}% от стоимости (бензин, ${engineVolume <= 2800 ? '≤2800' : '>2800'} см³)`;
      }
    } else if (carAge === "3to5" || carAge === "5to7") {
      const table = isDiesel ? DUTY_DIESEL_3TO7 : DUTY_PETROL_3TO7;
      const row = findBracket(table, engineVolume, "maxCc");
      dutyDetails = `max(${(row.pct * 100).toFixed(0)}% × ${fmt(Math.round(carPriceEur))} €; ${row.minPerCc} €/см³ × ${fmt(engineVolume)} см³) × ${fmt(eurRate, 2)} ₽/€`;
    } else {
      const table = isDiesel ? DUTY_DIESEL_OVER7 : DUTY_PETROL_OVER7;
      const row = findBracket(table, engineVolume, "maxCc");
      dutyDetails = `${row.rate} €/см³ × ${fmt(engineVolume)} см³ × ${fmt(eurRate, 2)} ₽/€`;
    }

    const excise = calcExcise(enginePower);

    const vat = Math.round((carPriceRub + duty + excise) * VAT_RATE);
    const vatDetails = `22% × (стоимость + пошлина + акциз)`;

    breakdown.push({ label: "Таможенная пошлина", value: duty, details: dutyDetails });
    breakdown.push({ label: "Акциз", value: excise, details: exciseDetails(enginePower) });
    breakdown.push({ label: "НДС 22%", value: vat, details: vatDetails });
    customsTotal = duty + excise + vat;
  }

  // 4. Утильсбор — по справочнику ПП 1291 в кВт (ДВС: мощность двигателя; гибрид с известными
  //    ДВС и мотором: ДВС + 30-мин мотора; EV/EREV: 30-минутная).
  const isNew = carAge === "under3";
  const recyclingResult = calcRecyclingFeeKw({
    isEv,
    powerKw: thirtyMin ? thirtyMin.kw : hybridUtil ? hybridUtil.totalKw : hpToKw(enginePower),
    volumeCc: engineVolume,
    isNew,
    buyerType,
    personalUse,
  });
  breakdown.push({ label: "Утилизационный сбор", value: recyclingResult.fee, details: recyclingDetails(recyclingResult, isNew, thirtyMin, hybridUtil) });

  // 5. Фиксированные расходы — только если country указана
  let fixedTotal = 0;

  if (country) {
    if (isChina) {
      const markupRub = FIXED_COSTS.china.markup_rub;
      breakdown.push({ label: `Доставка до ${city}, СБКТС, ЭПТС, брокер`, value: markupRub });
      fixedTotal = markupRub;
    } else if (isKorea) {
      const k = FIXED_COSTS.korea;
      // ₩-markup converted to ₽ at the same operational rate used for the car.
      const markupKrwInRub = Math.round(k.importMarkupKrw * currencyRate);
      const servicesTotal = markupKrwInRub + k.brokerRub + k.commissionRub;
      breakdown.push({ label: "Услуги по импорту и доставке", value: servicesTotal });
      fixedTotal = servicesTotal;
    } else if (isJapan) {
      const j = FIXED_COSTS.japan;
      // ¥-markup converted to ₽ at the same operational rate used for the car.
      const markupJpyInRub = Math.round(j.importMarkupJpy * currencyRate);
      const servicesTotal = markupJpyInRub + j.brokerRub + j.commissionRub;
      breakdown.push({ label: "Услуги по импорту и доставке", value: servicesTotal });
      fixedTotal = servicesTotal;
    }
  }

  // 6. Итого
  const totalRub =
    carPriceRub +
    processingFee +
    customsTotal +
    recyclingResult.fee +
    fixedTotal;

  return {
    totalRub,
    carPriceRub,
    breakdown,
    currencyRate: {
      code: currencyCode,
      rate: currencyRate,
      eurRate,
      date: rates.date,
    },
    deliveryCity: city,
    servicesTotalRub: fixedTotal,
  };
}

export function formatPrice(price: number): string {
  return price.toLocaleString("ru-RU") + " \u20BD";
}
